// src/components/GeneralHeader.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import logo from "../assets/images/logo.png";
import { API_BASE_URL } from "../utils/api";
import "../styles/GeneralHeader.css";
import { motion } from "framer-motion";

const GeneralHeader = ({ currentView, setCurrentView }) => {
  const token = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);

  // NEW: unread notifications count (auth only)
  const [unreadNotiCount, setUnreadNotiCount] = useState(0);

  // ---------- nav helpers ----------
  const pathToView = (path) => {
    switch (path) {
      case "/": return "home";
      case "/cart": return "cart";
      case "/favorites": return "favorites";
      case "/saved-cars": return "savedCars";
      case "/orders": return "orders";
      case "/notifications": return "notifications";
      case "/account": return "account";
      case "/login": return "login";
      default: return "home";
    }
  };

  const handleNavigateClick = (e, path) => {
    e.preventDefault();
    setCurrentView && setCurrentView(pathToView(path));
    setIsMobileOpen(false);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE_URL}/logout`, {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch {
      // ignore
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      setCartCount(0);
      setUnreadNotiCount(0); // NEW: clear on logout
      setCurrentView && setCurrentView("default");
      setIsMobileOpen(false);
      // optionally inform other components to recount
      try { window.dispatchEvent(new Event("cart:recount")); } catch { }
      try { window.dispatchEvent(new Event("notifications:recount")); } catch { }
    }
  };

  // ---------- cart count helpers ----------
  const readGuestCount = () => {
    try {
      const raw = localStorage.getItem("cart");
      const arr = raw ? JSON.parse(raw) : [];
      return Array.isArray(arr) ? arr.length : 0;
    } catch {
      return 0;
    }
  };

  // Normalize various API shapes to an array of carts
  const extractCarts = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (payload?.data && typeof payload.data === "object") return [payload.data];
    if (payload && typeof payload === "object" && Array.isArray(payload.items)) return [payload];
    return [];
  };

  // Count items with rules:
  // - If cart.total_quantity > 0, use it.
  // - Else, sum items[].quantity but treat 0/undefined as 1.
  const deriveCount = (payload) => {
    const carts = extractCarts(payload);
    
    return carts.reduce((sum, cart) => {
      // const tq = Number(cart?.total_quantity);
      // if (tq > 0) return sum + tq;
      const items = Array.isArray(cart?.items) ? cart.items : [];
      const itemsSum = items.reduce((s, it) => {
        const q = Number(it?.quantity);
        return s + (q > 0 ? q : 1);
      }, 0);
      return sum + itemsSum;
    }, 0);
  };

  const fetchAuthedCount = async () => {
    
    
    try {
      const { data } = await axios.get(`${API_BASE_URL}/carts/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data && data.status === false) throw new Error("Unauthenticated");
      setCartCount(deriveCount(data));
    } catch {
      // fallback to guest count if auth fails/401/shape issues
      setCartCount(readGuestCount());
    }
  };

  // ---------- notifications helpers (NEW) ----------
  const safeArray = (maybeArr) => (Array.isArray(maybeArr) ? maybeArr : []);
  const deriveUnreadNotifications = (payload) => {
    const items =
      safeArray(payload?.data?.items) ||
      safeArray(payload?.items) ||
      [];
    return items.filter((n) => n && n.isRead === false).length;
  };

  const fetchUnreadNotifications = async () => {
    if (!token) {
      setUnreadNotiCount(0);
      return;
    }
    try {
      const { data } = await axios.get(`${API_BASE_URL}/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (data && data.status === false) throw new Error("Unauthenticated");
      setUnreadNotiCount(deriveUnreadNotifications(data));
    } catch {
      // on any error, do not break UI; just show 0
      setUnreadNotiCount(0);
    }
  };

  // Initial load + token changes
  useEffect(() => {
    if (!token) {
      setCartCount(readGuestCount());
      setUnreadNotiCount(0); // NEW
      return;
    }
    fetchAuthedCount();
    fetchUnreadNotifications(); // NEW
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // React to localStorage changes across tabs (guest)
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "cart" && !token) setCartCount(readGuestCount());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [token]);

  // Allow manual recount: window.dispatchEvent(new Event("cart:recount"))
  useEffect(() => {
    const handler = () => {
      if (token) fetchAuthedCount();
      else setCartCount(readGuestCount());
    };
    window.addEventListener("cart:recount", handler);
    return () => window.removeEventListener("cart:recount", handler);
  }, [token]);

  // NEW: manual recount for notifications
  useEffect(() => {
    const handler = () => {
      if (token) fetchUnreadNotifications();
      else setUnreadNotiCount(0);
    };
    window.addEventListener("notifications:recount", handler);
    return () => window.removeEventListener("notifications:recount", handler);
  }, [token]);

  // ---------- links ----------
  const baseLinks = [
    { to: "/", label: "الصفحة الرئيسية" },
    { to: "/cart", label: "سلة التسوق" },
    { to: "/favorites", label: "القطع المفضلة" },
    { to: "/saved-cars", label: "سياراتي المحفوظة" },
    { to: "/orders", label: "طلبياتي" },
    { to: "/notifications", label: "الإشعارات" },
  ];
  const links = token
    ? [...baseLinks, { to: "/account", label: "حسابي" }]
    : [...baseLinks, { to: "/login", label: "تسجيل الدخول" }];

  const showCartBadge = Number(cartCount) > 0;
  const showNotiBadge = Number(unreadNotiCount) > 0;

  const goAbout = () => {
    if (typeof setCurrentView === "function") {
      setCurrentView("about");
    }
  };

  return (
    <header className="general-header" dir="rtl">
      <div className={`header-inner${currentView === 'cart' ? ' header-inner-cart' : ''}`}>

        {/* Logo */}
        {currentView !== "about" ? (
          <a
            href="/"
            className="logo-header"
            onClick={(e) => handleNavigateClick(e, "/")}
          >
            <img src={logo} alt="Partech" />
          </a>
        ) :
          <a
            href="/"
            className="logo-header"
            onClick={(e) => handleNavigateClick(e, "/")}
          >
          </a>
        }

        {currentView === "cart" ? (
          <motion.button
            className="back-pill-cart m-only"
            onClick={() => setCurrentView && setCurrentView("brands")}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          // style={{ position: "absolute", height: 40, top: 30, left: 80 }}
          >
            <span>تابع التسوق</span>
          </motion.button>
        ) :
          <button
            className="cart-icon-btn m-only"
            aria-label="سلة التسوق"
            onClick={(e) => handleNavigateClick(e, "/cart")}
            title="سلة التسوق"
          >
            <i className="bi bi-cart3" />
            {showCartBadge && <span className="cart-badge cart-badge--text">{cartCount}</span>}
          </button>
        }
        {/* Cart icon (mobile) */}


        {/* Burger (mobile) */}
        <button
          className="burger m-only"
          onClick={() => setIsMobileOpen((s) => !s)}
          aria-label="قائمة"
          aria-expanded={isMobileOpen}
          style={{ position: "relative" }}
        >
          <svg className="burger-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M3 6h18M3 12h18M3 18h18"
              fill="none"
              stroke="#CAEA3E"
              strokeWidth="2.6"
              strokeLinecap="round"
            />
          </svg>
          {/* NEW: unread badge on burger */}
          {showNotiBadge && (
            <span
              className="cart-badge cart-badge--text"
              style={{
                position: "absolute",
                top: -4,
                left: -4, // RTL: badge at left-top corner
                transform: "translate(0, 0)",
                minWidth: 18,
                height: 18,
                fontSize: 12,
                lineHeight: "17px",
                padding: "0 4px",
                borderRadius: "20px"
              }}
            >
              {unreadNotiCount > 99 ? "99+" : unreadNotiCount}
            </span>
          )}
        </button>

        {/* Desktop nav */}
        <nav className="nav-desktop" aria-label="الملاحة الرئيسية">
          <ul>
            {links.map(({ to, label }) => {
              const isActive = currentView === pathToView(to);
              const isLogin = label === "تسجيل الدخول";
              const isAccount = label === "حسابي";
              const isCart = to === "/cart";
              const isNotifications = to === "/notifications";
              return (
                <li key={to} className={isActive ? "active" : ""} style={{ position: "relative" }}>
                  <a
                    href={to}
                    className={`nav-link ${isLogin ? "login-pill" : ""} ${isCart ? "cart-link" : ""}`}
                    onClick={(e) => handleNavigateClick(e, to)}
                    style={{ position: "relative" }}
                  >
                    {isAccount && (
                      <i
                        className="bi bi-person-circle"
                        style={{ marginLeft: 6, color: "#caff0a", fontSize: "1.1em" }}
                      />
                    )}
                    {label}
                    {/* Cart badge (existing) */}
                    {isCart && showCartBadge && (
                      <span className="cart-badge cart-badge--text">{cartCount}</span>
                    )}
                    {/* NEW: Notifications badge (desktop link) */}
                    {isNotifications && showNotiBadge && (
                      <span
                        className="cart-badge cart-badge--text"
                        style={{
                          position: "absolute",
                          top: -6,
                          left: 50, // RTL: left-top corner of the link
                          minWidth: 18,
                          height: 18,
                          fontSize: 12,
                          lineHeight: "18px",
                          padding: "0 6px",
                        }}
                        aria-label={`لديك ${unreadNotiCount} إشعار غير مقروء`}
                        title={`${unreadNotiCount} غير مقروء`}
                      >
                        {unreadNotiCount > 99 ? "99+" : unreadNotiCount}
                      </span>
                    )}
                  </a>
                </li>
              );
            })}
            {token && (
              <li>
                <button type="button" className="nav-logout" onClick={handleLogout}>
                  تسجيل الخروج
                </button>
              </li>
            )}
          </ul>
        </nav>
      </div>

      {/* Mobile overlay + sheet */}
      <div
        className={`mobile-overlay ${isMobileOpen ? "show" : ""}`}
        onClick={() => setIsMobileOpen(false)}
      >
        <div className="mobile-sheet" dir="rtl" onClick={(e) => e.stopPropagation()}>
          <ul className="mobile-list">
            {[
              { to: "/", label: "الصفحة الرئيسية", icon: "bi bi-house" },
              { to: "/favorites", label: "القطع المفضلة", icon: "bi bi-heart" },
              { to: "/saved-cars", label: "سياراتي المحفوظة", icon: "bi bi-bookmark" },
              { to: "/orders", label: "طلبياتي", icon: "bi bi-layers" },
              // NOTE: we'll render the notifications row with a dynamic badge below
              // so we exclude it here and add it after the map
              ...(token ? [{ to: "/account", label: "حسابي", icon: "bi bi-person-circle" }] : []),
            ].map(({ to, label, icon }) => {
              const isActive = currentView === pathToView(to);
              return (
                <li key={to} className={isActive ? "active" : ""}>
                  <a href={to} className="mobile-row" onClick={(e) => handleNavigateClick(e, to)}>
                    <span className="icon-wrap">
                      <i className={icon} />
                    </span>
                    <span className="mobile-text">{label}</span>
                  </a>
                </li>
              );
            })}

            {/* NEW: Notifications row with dynamic unread badge */}
            <li className={currentView === "notifications" ? "active" : ""}>
              <a
                href="/notifications"
                className="mobile-row"
                onClick={(e) => handleNavigateClick(e, "/notifications")}
              >
                <span className="icon-wrap" style={{ position: "relative" }}>
                  <i className="bi bi-bell" />
                  {showNotiBadge ? (
                    <span
                      className="cart-badge cart-badge--text"
                      style={{
                        position: "absolute",
                        top: -4,
                        left: 20, // RTL
                        minWidth: 16,
                        height: 16,
                        fontSize: 12,
                        lineHeight: "14px",
                        padding: "0 4px",
                      }}
                    >
                      {unreadNotiCount > 99 ? "99+" : unreadNotiCount}
                    </span>
                  ) : (
                    // keep your subtle dot if you like when 0? the spec says only when > 0
                    null
                  )}
                </span>
                <span className="mobile-text">الإشعارات</span>
              </a>
            </li>

            {/* NEW: تعرف على (About) */}
            <li>
              <button
                type="button"
                className="mobile-row as-button"
                onClick={goAbout}
                title="تعرف على بارتك"
              >
                <span className="icon-wrap" style={{ marginRight: "5px" }}>
                  <i className="bi bi-question-circle" />
                </span>
                <span className="mobile-text" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  تعرف على
                  <img
                    src={logo}
                    alt="PARTECH"
                    style={{ height: 18, width: "auto", verticalAlign: "middle", marginTop: "5px" }}
                  />
                </span>
              </button>
            </li>

            {/* NEW: تابعنا (socials) */}
            <li>
              <div className="mobile-row static" style={{ paddingBottom: "0px" }}>
                <span className="icon-wrap">
                  <i className="bi bi-plus-square-dotted" />
                </span>
                <span className="mobile-text">تابعنا</span>
              </div>
              <div className="mobile-socials" style={{ paddingTop: "0px" }}>
                <a href="https://www.tiktok.com/@partech_ehfazha?_t=ZS-90081dETpGC&_r=1" target="blank" aria-label="TikTok" className="social-link">
                  <i className="bi bi-tiktok" />
                </a>
                <a href="https://www.instagram.com/partech_ehfazha/" target="blank" aria-label="Instagram" className="social-link">
                  <i className="bi bi-instagram" />
                </a>
                <a href="#" aria-label="Facebook" className="social-link text-muted">
                  <i className="bi bi-facebook" />
                </a>
                <a href="#" aria-label="X" className="social-link text-muted">
                  <i className="bi bi-twitter-x" />
                </a>
                <a href="#" aria-label="LinkedIn" className="social-link text-muted">
                  <i className="bi bi-linkedin" />
                </a>
                <a href="#" aria-label="Snapchat" className="social-link text-muted">
                  <i className="bi bi-snapchat" />
                </a>
              </div>
            </li>
          </ul>

          {token ? (
            <button className="mobile-auth-btn outline" onClick={handleLogout}>
              تسجيل الخروج
            </button>
          ) : (
            <a
              href="/login"
              className="mobile-auth-btn login-btn"
              onClick={(e) => handleNavigateClick(e, "/login")}
            >
              تسجيل الدخول
            </a>
          )}
        </div>
      </div>
    </header>
  );
};

export default GeneralHeader;
